/**
 * Dashboard Service
 *
 * API client for admin dashboard metrics and activity.
 * Blueprint Task: Wave 2c remediation
 */

import { getAccessToken } from '../lib/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface DashboardMetric {
  label: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  trend?: 'up' | 'down' | 'stable';
  unit?: string;
}

export interface DashboardActivity {
  id: string;
  type: 'customer' | 'fulfillment' | 'billing' | 'system' | 'ai';
  action: string;
  description: string;
  actor: string;
  actorType: 'human' | 'ai' | 'system';
  timestamp: Date;
  entityId?: string;
  entityType?: string;
  metadata?: Record<string, unknown>;
}

export interface DashboardAlert {
  id: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  actionUrl?: string;
}

export interface DashboardData {
  metrics: DashboardMetric[];
  activity: DashboardActivity[];
  alerts: DashboardAlert[];
  systemHealth: {
    status: 'healthy' | 'degraded' | 'critical';
    services: {
      name: string;
      status: 'operational' | 'degraded' | 'down';
      latency?: number;
    }[];
  };
  summary: {
    activeCustomers: number;
    pendingTasks: number;
    pendingProposals: number;
    revenueTotal: number;
    revenueYTD: number;
    revenueMTD: number;
    revenueThisMonth: number;
    sosRequestsPending: number;
  };
}

// ============================================================================
// Ops Summary Types (time-windowed operational metrics)
// ============================================================================

export interface TimeWindow {
  last24h: number;
  last48h: number;
  last72h: number;
}

export interface OpsSummary {
  timestamp: string;
  customers: { total: number; new: TimeWindow };
  revenue: { total: number; ytd: number; mtd: number; recent: TimeWindow };
  payments: { recent: TimeWindow };
  supportTickets: { unread: number; open: number; newRecent: TimeWindow };
  sosRequests: { outstanding: number; recent: TimeWindow };
  fulfillment: { pendingTotal: number; newRecent: TimeWindow };
}

function getMockOpsSummary(): OpsSummary {
  return {
    timestamp: new Date().toISOString(),
    customers: { total: 0, new: { last24h: 0, last48h: 0, last72h: 0 } },
    revenue: { total: 0, ytd: 0, mtd: 0, recent: { last24h: 0, last48h: 0, last72h: 0 } },
    payments: { recent: { last24h: 0, last48h: 0, last72h: 0 } },
    supportTickets: { unread: 0, open: 0, newRecent: { last24h: 0, last48h: 0, last72h: 0 } },
    sosRequests: { outstanding: 0, recent: { last24h: 0, last48h: 0, last72h: 0 } },
    fulfillment: { pendingTotal: 0, newRecent: { last24h: 0, last48h: 0, last72h: 0 } },
  };
}

/**
 * Fetches ops summary with time-windowed metrics
 */
export async function fetchOpsSummary(): Promise<OpsSummary> {
  if (!API_URL) {
    return getMockOpsSummary();
  }

  try {
    const token = await getAccessToken();
    const response = await fetch(`${API_URL}/admin/dashboard/ops-summary`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn('[DashboardService] ops-summary API error, using mock');
      return getMockOpsSummary();
    }

    const responseData = await response.json();
    return (responseData.data ?? responseData) as OpsSummary;
  } catch (err) {
    console.warn('[DashboardService] ops-summary fetch failed, using mock', err);
    return getMockOpsSummary();
  }
}

// ============================================================================
// Legacy Dashboard Types
// ============================================================================

/**
 * Mock dashboard data for when API isn't available
 */
function getMockDashboardData(): DashboardData {
  return {
    metrics: [],
    activity: [
      {
        id: '1',
        type: 'customer',
        action: 'customer_created',
        description: 'Customer Kevin Gordon (Seafood Smugglers LLC) active',
        actor: 'System',
        actorType: 'system',
        timestamp: new Date(Date.now() - 86400000), // 1 day ago
      },
      {
        id: '2',
        type: 'billing',
        action: 'payment_received',
        description: 'Payment of $600 received from Kevin Gordon',
        actor: 'Stripe',
        actorType: 'system',
        timestamp: new Date(Date.now() - 172800000), // 2 days ago
      },
    ],
    alerts: [],
    systemHealth: {
      status: 'healthy',
      services: [
        { name: 'Orders API', status: 'operational' },
        { name: 'Checkout API', status: 'operational' },
        { name: 'Magic Link', status: 'operational' },
      ],
    },
    summary: {
      activeCustomers: 4,
      pendingTasks: 0,
      pendingProposals: 0,
      revenueTotal: 1050,
      revenueYTD: 1050,
      revenueMTD: 0,
      revenueThisMonth: 0,
      sosRequestsPending: 0,
    },
  };
}

/**
 * Fetches all dashboard data
 */
export async function fetchDashboardData(): Promise<DashboardData> {
  // If no API URL configured, return mock data
  if (!API_URL) {
    console.info('[DashboardService] No API_URL configured, using mock data');
    return getMockDashboardData();
  }

  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // Fall back to mock data on error
    console.warn('[DashboardService] API error, falling back to mock data');
    return getMockDashboardData();
  }

  const responseData = await response.json();

  // Backend wraps response in { success, data, meta } envelope
  const data = responseData.data ?? responseData;

  // Backend returns summary stats, not full dashboard structure
  // Map backend response to frontend expected format
  return {
    metrics: data.metrics ?? [],
    activity: (data.activity ?? []).map((a: Record<string, unknown>) => ({
      ...a,
      timestamp: new Date(a.timestamp as string),
    })),
    alerts: (data.alerts ?? []).map((a: Record<string, unknown>) => ({
      ...a,
      timestamp: new Date(a.timestamp as string),
    })),
    systemHealth: data.systemHealth ?? {
      status: 'healthy',
      services: [],
    },
    summary: {
      activeCustomers: data.activeCustomers ?? 0,
      pendingTasks: data.pendingTasks ?? 0,
      pendingProposals: data.pendingProposals ?? 0,
      revenueTotal: data.revenueTotal ?? 0,
      revenueYTD: data.revenueYTD ?? 0,
      revenueMTD: data.revenueMTD ?? data.revenueThisMonth ?? 0,
      revenueThisMonth: data.revenueThisMonth ?? 0,
      sosRequestsPending: data.sosRequestsPending ?? 0,
    },
  };
}

/**
 * Fetches dashboard metrics only
 */
export async function fetchDashboardMetrics(): Promise<DashboardMetric[]> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/metrics`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch metrics: ${response.statusText}`);
  }

  const data = await response.json();
  return data.metrics;
}

/**
 * Fetches recent activity
 */
export async function fetchDashboardActivity(limit = 20): Promise<DashboardActivity[]> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/activity?limit=${limit}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch activity: ${response.statusText}`);
  }

  const data = await response.json();

  return data.activity.map((a: Record<string, unknown>) => ({
    ...a,
    timestamp: new Date(a.timestamp as string),
  }));
}

/**
 * Fetches dashboard alerts
 */
export async function fetchDashboardAlerts(): Promise<DashboardAlert[]> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/alerts`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch alerts: ${response.statusText}`);
  }

  const data = await response.json();

  return data.alerts.map((a: Record<string, unknown>) => ({
    ...a,
    timestamp: new Date(a.timestamp as string),
  }));
}

/**
 * Acknowledges an alert
 */
export async function acknowledgeAlert(alertId: string): Promise<void> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/alerts/${alertId}/acknowledge`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to acknowledge alert: ${response.statusText}`);
  }
}

/**
 * Fetches system health status
 */
export async function fetchSystemHealth(): Promise<DashboardData['systemHealth']> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/health`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch health: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches revenue analytics
 */
export async function fetchRevenueAnalytics(period: 'week' | 'month' | 'quarter' | 'year'): Promise<{
  total: number;
  change: number;
  byDay: { date: string; amount: number }[];
  byService: { service: string; amount: number }[];
}> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/revenue?period=${period}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch revenue: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches AI operations summary
 */
export async function fetchAIOperationsSummary(): Promise<{
  proposalsToday: number;
  proposalsApproved: number;
  proposalsRejected: number;
  signalsProcessed: number;
  avgConfidence: number;
  killSwitchStatus: boolean;
}> {
  const token = await getAccessToken();

  const response = await fetch(`${API_URL}/admin/dashboard/ai-operations`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch AI operations: ${response.statusText}`);
  }

  return response.json();
}
